import {deleteCookie, getCookie, setCookie} from '@github-ui/cookies'
import {useState} from 'react'

import {defaultViewConfig, type DefaultViewDashboardType} from '../constants/default-view'
import {persistPullsPreferences} from '../helpers/pulls-preferences-api'

const COOKIE_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000 // 1 year

/**
 * Manages the default view for a dashboard surface via a browser cookie.
 *
 * Pull request defaults are also persisted to the pulls preferences API. Issues
 * defaults are cookie-only because that API's `default_view` field is pulls-specific.
 */
export function useSaveDefaultView(
  dashboardType: DefaultViewDashboardType,
  currentViewId?: string,
  defaultViewId?: string,
) {
  const {cookieName, fallbackViewId} = defaultViewConfig[dashboardType]
  const [isSaving, setIsSaving] = useState(false)
  const [savedDefaultViewId, setSavedDefaultViewId] = useState<string | undefined>(() => {
    return getDefaultView(dashboardType) ?? (defaultViewId != null ? String(defaultViewId) : undefined)
  })

  const defaultViewValue = currentViewId != null && currentViewId !== '' ? String(currentViewId) : fallbackViewId
  const isDefault = defaultViewValue === (savedDefaultViewId || fallbackViewId)

  async function saveDefaultView() {
    if (isDefault) return

    setIsSaving(true)
    try {
      const expireTime = new Date(Date.now() + COOKIE_EXPIRY_MS).toUTCString()
      setCookie(cookieName, defaultViewValue, expireTime)
      setSavedDefaultViewId(defaultViewValue)
      persistDefaultPullsView(dashboardType, defaultViewValue)
    } catch (error) {
      setIsSaving(false)
      throw error
    }
    setIsSaving(false)
  }

  async function clearDefaultView() {
    setIsSaving(true)
    try {
      if (dashboardType === 'issues') {
        const expireTime = new Date(Date.now() + COOKIE_EXPIRY_MS).toUTCString()
        setCookie(cookieName, fallbackViewId, expireTime)
        setSavedDefaultViewId(fallbackViewId)
      } else {
        deleteCookie(cookieName)
        setSavedDefaultViewId(undefined)
      }
      persistDefaultPullsView(dashboardType, '')
    } catch (error) {
      setIsSaving(false)
      throw error
    }
    setIsSaving(false)
  }

  return {isSaving, isDefault, saveDefaultView, clearDefaultView}
}

export function getDefaultView(dashboardType: DefaultViewDashboardType): string | undefined {
  return getCookie(defaultViewConfig[dashboardType].cookieName)?.value
}

function persistDefaultPullsView(dashboardType: DefaultViewDashboardType, defaultView: string) {
  if (dashboardType === 'pull-requests') {
    persistPullsPreferences({default_view: defaultView})
  }
}
